// src/hooks/useRackTree.js
//
// S3-02.3 — the one data layer for the R2 racks page.
//
// THREE flat queries (racks+shelves, enclosures, clutches) — plus the
// small rooms list — are folded into the shape the page renders. There
// is deliberately no facility-wide ANIMALS query: every count on this
// page comes from `Enclosure.animals_count`, and the animals themselves
// load one enclosure at a time when a drawer opens (`loadAnimalsFor`).
//
//   racks[]        { ...rack, faces: [{ face, shelves: [{ ...shelf,
//                    tanks[], cups[], span, spanUsed, spanFree }] }] }
//   freestanding[] enclosures with no shelf — first-class, not a
//                  pseudo-rack (R2: "a freestanding enclosure is just
//                  rack=NULL")
//   unplacedClutches[]
//
// Free span is ARITHMETIC, per the spec ("Available area is ARITHMETIC,
// not rows"): shelf span minus the summed LENGTHS of its tanks. It is
// null — rendered "free", not "~N in free" — whenever any needed
// dimension is missing, because a wrong number is worse than no number.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getRacksAndEnclosuresByFacility,
  getEnclosures,
  getClutchesByFacility,
  getAnimalsByFacility,
  listRooms,
  moveEnclosure,
  placeAnimalGroup,
  rackApiError,
} from '../services/api';
import { roomIdOf, sortRooms } from '../components/Racks/rackHelpers';

/**
 * The room a facility falls back to when the API returns none — a
 * facility created before the rooms migration, or a rooms request that
 * failed. Its id is null, so nothing can be "moved into" it by mistake,
 * but every rack and tank still has somewhere to be drawn. Never
 * dropping a rack is the rule; inventing a REAL room is not.
 */
export const FALLBACK_MAIN_ROOM = {
  id: null,
  name: 'Frog Room',
  is_main: true,
  position: '',
  notes: '',
  is_placeholder: true,
};

/** Axios response / DRF pagination / bare array -> array. */
export const toArray = (resp) => {
  const payload = resp?.data ?? resp;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

export const idOf = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return value.id ? String(value.id) : null;
  return String(value);
};

export const numOr = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Has nobody counted this clutch yet? Then it is PRESENT, not empty.
 *
 * `archiving.PRESENT_GROUP`'s extra term, said on the client: no count row
 * at all, and none of the three flags that really do mean absent. NOT a
 * count of zero — that is the leftover slot the placeholder draws.
 */
export const isUncountedClutch = (clutch) => (
  clutch?.has_count_row === false
  && clutch?.is_split !== true
  && clutch?.is_deceased !== true
  && clutch?.is_in_possession !== false
);

/**
 * Is something LIVING in this clutch record?
 *
 * S3-03.5 — the server's answer, not ours: `list_clutches` annotates
 * `is_active` from `services/archiving.py OCCUPYING_GROUP` (latest count
 * above zero, not split, not deceased, in possession), so the ONE
 * definition of occupancy stays in one file. Only active records may
 * appear in "Unplaced clutches" — after the legacy tubs were archived
 * the strip announced fifteen set-aside shells, and the operator wants
 * none of it on this page. A PLACED cup is untouched by this: it still
 * holds its spot on its shelf even when empty (S3-03.4).
 *
 * A row from an older backend has no `is_active` at all — then it counts
 * as active, because dropping rows on the strength of a field the server
 * never sent would empty the list rather than tidy it.
 *
 * ⚠️ S3-05.3 review round 2 — "NOT COUNTED YET" IS NOT "ZERO LEFT", AND
 * THIS IS THE CLIENT BELT FOR IT. The server's rule is
 * `archiving.PRESENT_GROUP` now (the occupancy rule widened by "no count
 * row at all"), so `is_active` already answers correctly.
 *
 * ⚠️ AND WHAT THE BELT GUARDS IS A SERVER THAT SENDS `has_count_row: false`
 * BESIDE A STALE `is_active` — not an older API (review round 3, item 3).
 * `isPresentAnimal`'s belt reads `is_deceased` / `is_in_possession`, columns
 * that have always been on the wire, so it really does survive a bundle
 * reaching a browser ahead of the API. This one cannot: `has_count_row` is
 * the field round 2 ADDED, so on an older API it is absent, the clause is
 * False, and a fresh clutch is drawn as a leftover exactly as it was. The
 * deploy-order case is not what it covers.
 *
 * What it does cover is one line: `_is_active` in `list_clutches` is
 * annotated from `PRESENT_GROUP`, two lines away from the `OCCUPYING_GROUP`
 * the `active=1` filter still asks — one word apart, and a revert or a
 * merge that puts the archive's rule back on that annotation (or a future
 * unannotated path whose `is_active` falls back to the blocking rule) sends
 * `has_count_row: false` beside `is_active: false`. Then this clause is the
 * difference between a brand-new, never-counted clutch being DRAWN and being
 * drawn as an empty slot. It only ever says MORE is present, never less,
 * and a row whose `has_count_row` the server never sent is classified
 * exactly as it was before.
 */
export const isActiveClutch = (clutch) => (
  clutch?.is_active !== false || isUncountedClutch(clutch)
);


/**
 * Is this animal PHYSICALLY IN THE TANK? `archiving.PRESENT_ANIMAL`.
 *
 * S3-05.3 fix pass 1, item 5 — THE CLIENT BELT, and it is kept for the
 * same reason `partitionClutches` is kept on the clutch side: the API and
 * the frontend deploy SEPARATELY, and a bundle that reaches a browser
 * before the API carries `?present=1` would silently put deceased and
 * shipped-out animals back on the board — the ruling reverted by a
 * deploy order. The rule is the SERVER'S (`?present=true`, one expression
 * in the database, which is why the payload is lighter); this is one line
 * that cannot make it wrong.
 *
 * A row from an older backend may carry neither flag: like
 * `isActiveClutch`, an ABSENT flag counts as present, because dropping
 * rows on the strength of a field the server never sent would empty a
 * tank rather than tidy it.
 *
 * ⚠️ NOT `LIVE_ANIMAL`: a SOLD animal awaiting shipment is standing in
 * the tank and is drawn, with `rackHelpers.presenceChip` on its row.
 */
export const isPresentAnimal = (animal) => (
  animal?.is_deceased !== true && animal?.is_in_possession !== false
);

/**
 * One list of clutch rows, split by that SAME rule. `{ active, inactive }`.
 *
 * S3-02.8 — a tank card counted and listed the finished records as
 * occupants: prod tank 3.1C read "0 animals · 4 clutches" over four
 * split shells at Count: 0, while the ARCHIVE was happy to call the same
 * tank empty (occupancy has ignored those rows since the 2026-09-08
 * ruling). A card that says four where the action says zero is the page
 * disagreeing with itself.
 *
 * This is `isActiveClutch` applied to a list and nothing more — there is
 * still exactly ONE copy of the rule, and it is the server's
 * (`services/archiving.py OCCUPYING_GROUP`, serialized as `is_active`).
 * A THIRD copy is precisely how "the card says 4, the archive says 0"
 * started.
 *
 * ⚠️ IT PARTITIONS RATHER THAN FILTERING, AND THE `inactive` HALF HAS
 * EXACTLY ONE CONSUMER (S3-05.3 fix pass 1, item 7): the PLACED CUPS on a
 * shelf, which `buildRackTree` splits with this function into the cups it
 * draws and the SLOT-RESERVATION PLACEHOLDERS it draws instead
 * (`CupPlaceholder`). Nothing else reads it — a clutch inside a TANK that
 * is not occupying is simply not drawn, because the server's
 * `present=true` never sent it and the ruling of 2026-09-26 says it is
 * clutter. The half is not dead and it is not a second rule: it is
 * `isActiveClutch` said about a list.
 *
 * (It USED to feed `InactiveClutches.jsx`, the collapsed disclosure on the
 * card — deleted with that component.)
 */
export const partitionClutches = (clutches) => {
  const active = [];
  const inactive = [];
  (clutches || []).forEach((clutch) => {
    (isActiveClutch(clutch) ? active : inactive).push(clutch);
  });
  return { active, inactive };
};

/** The enclosure an animal or clutch sits in, whatever key the API used. */
export const occupantEnclosureId = (row) =>
  idOf(row?.enclosure_id ?? row?.enclosureId ?? row?.enclosures_id ?? row?.enclosure);

// TOP SHELF FIRST — **DESCENDING** `shelf_no`. SHELF 1 IS THE BOTTOM
// (operator ruling 2026-09-15 evening, reversing 2026-09-14): every rack
// in the room was built bottom-up and the tank names already encode it
// ("3.1C" is rack 3, BOTTOM shelf, slot C), so the APP flips and NO DATA
// MOVES — nothing is renumbered and nothing is renamed.
//
// It is sorted HERE, once, because every surface that draws a rack reads
// `face.shelves` off this tree: RackDetail, PhoneRackDetail, the room
// map's rack silhouette, the label sheet's grouping and the rack
// editor's shelf list. A sort per component is how two of them end up
// drawing one rack in opposite directions — the exact failure the
// deleted per-browser "Shelf 1 is top / bottom" toggle caused.
const byShelfNoDesc = (a, b) => (b.shelf_no ?? 0) - (a.shelf_no ?? 0);
const byPosition = (a, b) =>
  (numOr(a.position_no, 9999) - numOr(b.position_no, 9999)) ||
  String(a.name || '').localeCompare(String(b.name || ''));

/**
 * Build the render tree. Exported separately from the hook so the tree
 * maths is testable without mounting a component.
 */
export function buildRackTree({
  racks = [], enclosures = [], clutches = [], rooms = [],
}) {
  // NIT 20: an enclosure or cup whose shelf id belongs to no rack in the
  // payload (a rack the caller filtered out, a mid-flight delete) used to
  // vanish from the page entirely — invisible AND unrecoverable, since
  // every Move… control hangs off a rendered box. Anything whose shelf we
  // cannot resolve falls back to the freestanding bucket, loudly.
  const knownShelfIds = new Set();
  (racks || []).forEach((rack) => (rack.shelves || []).forEach(
    (shelf) => knownShelfIds.add(idOf(shelf.id))));

  const tanksByShelf = new Map();
  const freestanding = [];
  (enclosures || []).forEach((enc) => {
    const shelfId = idOf(enc.shelf);
    if (!shelfId) {
      freestanding.push(enc);
      return;
    }
    if (!knownShelfIds.has(shelfId)) {
      console.warn(
        '[racks] enclosure %s references shelf %s which is on no known rack — '
        + 'showing it as freestanding so it stays reachable',
        enc.id, shelfId,
      );
      freestanding.push({ ...enc, orphaned_shelf: shelfId });
      return;
    }
    if (!tanksByShelf.has(shelfId)) tanksByShelf.set(shelfId, []);
    tanksByShelf.get(shelfId).push(enc);
  });

  const cupsByShelf = new Map();
  const unplacedClutches = [];
  (clutches || []).forEach((clutch) => {
    const shelfId = idOf(clutch.shelf);
    if (shelfId && knownShelfIds.has(shelfId)) {
      if (!cupsByShelf.has(shelfId)) cupsByShelf.set(shelfId, []);
      cupsByShelf.get(shelfId).push(clutch);
      return;
    }
    if (shelfId) {
      // The warning comes FIRST and unconditionally (review): a broken
      // shelf reference is a bug whether or not the row is still alive,
      // and going quiet about the inactive ones would hide half of it.
      console.warn(
        '[racks] clutch %s references shelf %s which is on no known rack — %s',
        clutch.id, shelfId,
        isActiveClutch(clutch)
          ? 'listing it as unplaced so it stays reachable'
          : 'it is a finished record, so this page does not show it',
      );
      if (isActiveClutch(clutch)) {
        unplacedClutches.push({ ...clutch, orphaned_shelf: shelfId });
      }
      return;
    }
    // A clutch inside a tank is drawn in that tank's drawer, not here.
    if (!occupantEnclosureId(clutch) && isActiveClutch(clutch)) {
      unplacedClutches.push(clutch);
    }
  });

  const builtRacks = (racks || []).map((rack) => {
    const shelves = Array.isArray(rack.shelves) ? [...rack.shelves] : [];
    const faceNames = rack.is_two_sided
      ? Array.from(new Set(shelves.map((s) => s.face || ''))).sort()
      : [''];
    const faces = (faceNames.length ? faceNames : ['']).map((face) => {
      const faceShelves = shelves
        .filter((s) => (s.face || '') === face)
        .sort(byShelfNoDesc)
        .map((shelf) => {
          const shelfId = idOf(shelf.id);
          const tanks = (tanksByShelf.get(shelfId) || []).slice().sort(byPosition);
          // S3-05.3 fix pass 1, item 1 — A PLACED CUP THAT IS NOT
          // OCCUPYING IS NOT A CLUTCH ON THIS PAGE. The server's
          // `present=true` sends `OCCUPYING_GROUP | (shelf IS NOT NULL)`,
          // so the second half arrives here: a deceased cup, a
          // shipped-out cup, a count-0 cup and a split cup all sit on
          // shelves in real facilities, and drawing them as full
          // `CupChip`s is exactly the clutter the ruling of 2026-09-26
          // names ("if it's completely split, if there's zero left …
          // get them off of there").
          //
          // They cannot simply be DROPPED, because
          // `placement.place_animal_group` reserves `(shelf, position_no)`
          // for any placed cup whatever its count — a slot that draws
          // nothing is a `position_occupied` refusal over empty space
          // (S3-02.6). So the tree splits them: `cups` are drawn and
          // counted, `cupPlaceholders` are drawn as the slim muted
          // leftover with one affordance (Remove) and counted NOWHERE.
          //
          // `placedCups` is the UNION and exists for the two readers that
          // must see the slot whichever half it is in: the placement
          // picker's "N — occupied by …" list, and the row's own width
          // budget.
          const placedCups = (cupsByShelf.get(shelfId) || []).slice().sort(byPosition);
          const { active: cups, inactive: cupPlaceholders } =
            partitionClutches(placedCups);
          const span = numOr(shelf.effective_span, numOr(shelf.width, numOr(rack.length)));
          const lengths = tanks.map((t) => numOr(t.length));
          const dimsKnown = span !== null && lengths.every((l) => l !== null);
          const spanUsed = dimsKnown ? lengths.reduce((a, b) => a + b, 0) : null;
          const spanFree = dimsKnown ? Math.max(0, span - spanUsed) : null;
          // NIT 14: an over-stuffed shelf reported "~0 in free", which reads
          // as "exactly full" rather than "you have described more tank than
          // shelf". Carry the overflow so the row can say so.
          const spanOver = dimsKnown && spanUsed > span ? spanUsed - span : null;
          const tankHeights = tanks.map((t) => numOr(t.height)).filter((h) => h !== null);
          const clearance = numOr(shelf.clearance_height,
            tankHeights.length ? Math.max(...tankHeights) : null);
          return {
            ...shelf,
            rack_id: idOf(rack.id),
            rack_name: rack.name,
            tanks,
            cups,
            cupPlaceholders,
            placedCups,
            span,
            spanUsed,
            spanFree,
            spanOver,
            clearance,
            dimsKnown,
          };
        });
      return { face, shelves: faceShelves };
    });
    const tankCount = faces.reduce(
      (n, f) => n + f.shelves.reduce((m, s) => m + s.tanks.length, 0), 0);
    // OCCUPYING cups only — a slot-reservation placeholder is counted in
    // NOTHING (S3-05.3 fix pass 1, item 1), here or in the shelf label or
    // in the room map's tile.
    const cupCount = faces.reduce(
      (n, f) => n + f.shelves.reduce((m, s) => m + s.cups.length, 0), 0);
    return { ...rack, faces, tankCount, cupCount };
  });

  const sortedFreestanding = freestanding.slice().sort(
    (a, b) => String(a.name || '').localeCompare(String(b.name || '')));

  // --- rooms (S3-02.3b) --------------------------------------------
  //
  // The tree is grouped by ROOM, main first. Anything whose room is
  // unknown — never filed, or filed into a room this payload does not
  // carry — falls into the MAIN room rather than disappearing: the same
  // never-drop rule the orphaned-shelf fallback above follows. On the
  // map it lands in that room's "Elsewhere" zone, one click from being
  // placed properly.
  const orderedRooms = sortRooms(rooms);
  const mainRoom = orderedRooms.find((r) => r.is_main)
    || orderedRooms[0]
    || FALLBACK_MAIN_ROOM;
  const roomList = orderedRooms.length ? orderedRooms : [mainRoom];
  const known = new Set(roomList.map((r) => String(r.id)));
  const bucketFor = (thing) => {
    const id = roomIdOf(thing);
    return id && known.has(id) ? id : String(mainRoom.id);
  };

  const byRoom = new Map(roomList.map((room) => [String(room.id), {
    ...room,
    racks: [],
    freestanding: [],
  }]));
  builtRacks.forEach((rack) => byRoom.get(bucketFor(rack)).racks.push(rack));
  // EVERY freestanding tank is bucketed, orphaned ones included: the
  // room's list is what makes a tank reachable, and dropping one here
  // would hide it completely. A tank whose shelf resolved to no known
  // rack is a BROKEN REFERENCE rather than a placement, so the MAP
  // declines to draw it as freestanding (RoomMap), but the list still
  // carries it — loudly.
  sortedFreestanding.forEach(
    (tank) => byRoom.get(bucketFor(tank)).freestanding.push(tank));

  return {
    racks: builtRacks,
    freestanding: sortedFreestanding,
    rooms: Array.from(byRoom.values()),
    unplacedClutches,
  };
}

// In-flight de-dupe. SplitClutchModal mounts one EnclosurePicker per
// split row and each picker owns a useRackTree, so N rows opening at
// once used to fire 4N requests. Concurrent loads for the same facility
// now share one round trip; the entry is dropped as soon as it settles,
// so nothing is ever served stale.
const inFlight = new Map();

const loadFacilityData = (facilityId, { clutches, force = false }) => {
  // SHOULD-FIX 6: the picker consumers only need the placement skeleton.
  // The animals query in particular is facility-wide and unbounded, and
  // SplitClutchModal mounts one picker per split row. The cache key
  // carries the shape so a picker's cheap load is never handed to the
  // board (which needs all four) or vice versa.
  const key = `${facilityId}|${clutches ? 'c' : ''}`;
  // `force` skips the share: a caller that has just CHANGED something
  // must not be handed a round trip that started before the change.
  if (!force && inFlight.has(key)) return inFlight.get(key);
  const promise = Promise.all([
    getRacksAndEnclosuresByFacility(facilityId),
    getEnclosures(facilityId),
    // ONE clutch feed, two questions (S3-02.6 + S3-03.5, superseded by
    // the param below): the board must draw whatever OCCUPIES a position,
    // and an emptied cup still holds its spot (`services/placement.py`
    // counts it). Without that the shelf looked free and a drop there
    // failed `position_occupied` naming an occupant nobody could see. The
    // Clutches page keeps the DEFAULT (finished clutches hidden) — this is
    // the racks board's feed, and it is a different question.
    //
    // S3-05.3 (operator ruling 2026-09-26, "get them off of there"):
    // `present=true` — the board draws only what is THERE. It is
    // `OCCUPYING_GROUP` (the one definition, `services/archiving.py`)
    // plus the cups that are PHYSICALLY ON A SHELF, because a placed cup
    // reserves its `position_no` whatever its count and this board is
    // the only surface that can unplace one. The filter is the SERVER'S
    // now, not this file's: a tub full of history is no longer shipped
    // for the page to throw away, and there is one rule instead of two.
    clutches
      ? getClutchesByFacility(facilityId, { present: 'true' })
      : Promise.resolve([]),
    // COWORK RUN 3: the facility-wide animals query took 25-30s and the
    // page rendered NOTHING until it came back — a blank screen where a
    // room should be, because a count was waiting on every animal in
    // the building. It is gone: EnclosureSerializer already ships
    // `animals_count` per tank, which is every count this page draws.
    // The animals themselves are fetched per enclosure, when a drawer
    // is actually opened (`loadAnimalsFor`).
    //
    // The rooms list is small and never fails the page: a facility whose
    // rooms cannot be read still draws its racks, in the fallback main
    // room. Rooms are structure, not the inventory.
    // A rooms failure returns null, NOT [] — an empty list is a real
    // answer ("this facility has no rooms") and would collapse the plan
    // to the placeholder room. Null means "no news", and the caller
    // keeps what it already had.
    Promise.resolve(listRooms(facilityId)).catch((err) => {
      console.warn('[racks] room list failed:', err?.message);
      return null;
    }),
  ]).finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
};

export default function useRackTree(facilityId, options = {}) {
  const wantClutches = options.clutches !== false;
  const [racks, setRacks] = useState([]);
  const [rooms, setRooms] = useState([]);
  // S3-02.5: the archived side of the room rides along in the racks
  // payload rather than costing the Archived strip two GETs of its own
  // on every mount. Two flat lists, refreshed with everything else.
  const [archivedRacks, setArchivedRacks] = useState([]);
  const [archivedTanks, setArchivedTanks] = useState([]);
  const [enclosures, setEnclosures] = useState([]);
  const [clutches, setClutches] = useState([]);
  // Filled ONE ENCLOSURE AT A TIME, as drawers open. Never facility-wide.
  const [animalsByEnclosure, setAnimalsByEnclosure] = useState(() => new Map());
  // The STATE of each of those fetches, which is a different question
  // from the answer. Without it "never asked", "asked and failed" and
  // "asked, and the tank is empty" are the same empty array — and the
  // drawer said "Loading occupants…" forever for two of the three.
  // id -> { state: 'loading' | 'loaded' | 'error', error?: string }
  const [animalsStatus, setAnimalsStatus] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const aliveRef = useRef(true);

  // Mirrors of the two collections an optimistic move touches. React 18
  // defers a functional setState until render, which is AFTER an awaited
  // API call rejects — reading the pre-move snapshot out of the updater
  // therefore handed the revert path a null. These refs are written
  // synchronously so a rejected move always has something to put back.
  const enclosuresRef = useRef([]);
  const clutchesRef = useRef([]);
  // Read synchronously by loadAnimalsFor: a functional setState is
  // deferred to render, which is far too late to de-dupe a second call
  // made in the same tick.
  const animalsStatusRef = useRef(new Map());

  const writeEnclosures = useCallback((next) => {
    const value = typeof next === 'function' ? next(enclosuresRef.current) : next;
    enclosuresRef.current = value;
    setEnclosures(value);
  }, []);

  const writeClutches = useCallback((next) => {
    const value = typeof next === 'function' ? next(clutchesRef.current) : next;
    clutchesRef.current = value;
    setClutches(value);
  }, []);

  // BLOCKER 1 (adversarial review): src/index.js wraps the app in
  // <React.StrictMode>, so React 18 mounts, unmounts and REMOUNTS every
  // component in dev. The old cleanup-only version of this effect set
  // aliveRef false on that throwaway unmount and never re-armed it, so
  // every guard below short-circuited and `loading` stayed true forever —
  // a permanent spinner on the racks page and in all six picker modals in
  // any `npm start` session. Arming on mount is what makes the remount
  // behave like the first mount.
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // SHOULD-FIX 22: a post-write refresh used to flip `loading` true, which
  // replaced the whole drawing with a spinner and threw away the
  // operator's scroll position after every edit. Only the FIRST load
  // blanks the board; later ones set `refreshing` and the page keeps
  // rendering the tree it already has.
  const refresh = useCallback(async ({ initial = false, force = false } = {}) => {
    if (!facilityId) return;
    if (initial) setLoading(true); else setRefreshing(true);
    setError(null);
    try {
      const [rackRows, encRows, clutchResp, roomRows] =
        await loadFacilityData(facilityId, { clutches: wantClutches, force });
      if (!aliveRef.current) return;
      setRacks(toArray(rackRows));
      // Present only on the enveloped response; an older backend returns
      // a bare array and simply has nothing archived to report.
      const rackPayload = (rackRows && rackRows.data) || rackRows || {};
      setArchivedRacks(Array.isArray(rackPayload.archived_racks)
        ? rackPayload.archived_racks : []);
      setArchivedTanks(Array.isArray(rackPayload.archived_tanks)
        ? rackPayload.archived_tanks : []);
      if (roomRows !== null) setRooms(toArray(roomRows));
      // Whatever a drawer was told before this refresh may no longer be
      // true, so the answers are dropped and re-asked on next open.
      animalsStatusRef.current = new Map();
      setAnimalsStatus(new Map());
      writeEnclosures(toArray(encRows));
      // S3-05.3: this line USED to drop the deceased and the
      // not-in-possession rows here, after the server had sent them —
      // a second copy of the presence rule, in the client, over a
      // payload it had already paid for. `present=true` above is the
      // one rule and it runs in the database.
      writeClutches(toArray(clutchResp));
    } catch (err) {
      if (!aliveRef.current) return;
      setError(rackApiError(err));
    } finally {
      if (aliveRef.current) { setLoading(false); setRefreshing(false); }
    }
  }, [facilityId, wantClutches, writeEnclosures, writeClutches]);

  useEffect(() => { refresh({ initial: true }); }, [refresh]);

  const setStatus = useCallback((key, entry) => {
    setAnimalsStatus((prev) => {
      const next = new Map(prev);
      if (entry === null) next.delete(key); else next.set(key, entry);
      return next;
    });
  }, []);

  /**
   * Fetch the animals of ONE enclosure, the first time a drawer asks.
   *
   * CACHED: a second open of the same drawer costs nothing (pass
   * `{ force: true }` to re-ask, which is what a move does). Concurrent
   * asks are de-duped by the same status entry. EVERY outcome writes a
   * status — including the empty answer — so the drawer can tell
   * "nothing in it" from "still asking" from "that failed".
   */
  const loadAnimalsFor = useCallback(async (enclosureId, { force = false } = {}) => {
    const key = idOf(enclosureId);
    if (!key || !facilityId) return;
    const current = animalsStatusRef.current.get(key);
    if (current?.state === 'loading') return;
    if (current?.state === 'loaded' && !force) return;

    animalsStatusRef.current.set(key, { state: 'loading' });
    setStatus(key, { state: 'loading' });
    try {
      // S3-05.3 — `present=true` is `archiving.PRESENT_ANIMAL` in the
      // DATABASE: not deceased, in possession, and a SOLD animal
      // awaiting shipment still counts because it is standing in the
      // tank. THE RULE RUNS IN THE QUERY, which is why the payload is
      // lighter than the whole history of a tub.
      //
      // ⚠️ AND THE ONE-LINE BELT STAYS (fix pass 1, item 5), exactly as
      // `partitionClutches` stays on the clutch side. It is not a second
      // RULE — `isPresentAnimal` is the same two flags, and an absent
      // flag still counts as present — it is the guard against a DEPLOY
      // ORDER: the bundle and the API ship separately, and a browser
      // holding this code against an API that does not know `present`
      // would put every deceased and shipped-out row back on the board,
      // reverting the operator's ruling with no code change anywhere.
      // What must NOT be re-derived is the NUMBER: the card's face reads
      // `animals_count` off the server, and two derivations of one count
      // is how the card came to say four where the archive said zero.
      const rows = toArray(await getAnimalsByFacility(
        facilityId, { enclosure: key, present: 'true' })).filter(isPresentAnimal);
      if (!aliveRef.current) return;
      setAnimalsByEnclosure((prev) => {
        const next = new Map(prev);
        next.set(key, rows);
        return next;
      });
      animalsStatusRef.current.set(key, { state: 'loaded' });
      setStatus(key, { state: 'loaded' });
    } catch (err) {
      const message = rackApiError(err).message;
      console.warn('[racks] could not load animals for %s: %s', key, message);
      if (!aliveRef.current) return;
      animalsStatusRef.current.set(key, { state: 'error', error: message });
      setStatus(key, { state: 'error', error: message });
    }
  }, [facilityId, setStatus]);

  /** Forget one enclosure's answer, so the next open re-asks. */
  const invalidateAnimalsFor = useCallback((enclosureId) => {
    const key = idOf(enclosureId);
    if (!key) return;
    animalsStatusRef.current.delete(key);
    setStatus(key, null);
  }, [setStatus]);

  const clutchesByEnclosure = useMemo(() => {
    const map = new Map();
    clutches.forEach((c) => {
      const key = occupantEnclosureId(c);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return map;
  }, [clutches]);

  /**
   * Forget a room the server has just deleted, without waiting for the
   * refresh round trip. Cowork run 3: it stayed drawn on the plan until
   * a reload. Anything filed in it falls back to the main room, which is
   * the same never-drop rule the tree already follows.
   */
  const dropRoom = useCallback((roomId) => {
    const key = idOf(roomId);
    if (!key) return;
    setRooms((prev) => prev.filter((room) => String(room.id) !== key));
  }, []);

  const tree = useMemo(
    () => buildRackTree({ racks, enclosures, clutches, rooms }),
    [racks, enclosures, clutches, rooms],
  );

  const shelvesById = useMemo(() => {
    const map = new Map();
    tree.racks.forEach((rack) => rack.faces.forEach((face) => face.shelves.forEach(
      (shelf) => map.set(idOf(shelf.id), { ...shelf, rack }))));
    return map;
  }, [tree]);

  /**
   * Optimistic move of a tank. Applies locally, calls the API, and puts
   * the previous placement back if the server refuses (409
   * position_occupied / 400 face_not_allowed). Resolves to
   * `{ ok, error, enclosure }` — never throws, so drop handlers stay
   * simple.
   *
   * ⚠️ `enclosure` is the SERVER'S row, and the rename offer needs it:
   * a drop on a SHELF (rather than on a gap) sends no position at all
   * and the server appends, so "which slot did it land in?" is a
   * question only the response can answer. Reading it back off this
   * hook's state instead would race the `setState` that writes it.
   */
  const moveTank = useCallback(async (enclosureId, shelfId, positionNo) => {
    const key = idOf(enclosureId);
    const snapshot = enclosuresRef.current;
    writeEnclosures((prev) => prev.map((e) => (idOf(e.id) === key
      ? { ...e, shelf: shelfId ?? null, position_no: positionNo ?? null }
      : e)));
    try {
      const updated = await moveEnclosure(facilityId, key, { shelf: shelfId, positionNo });
      if (aliveRef.current) {
        writeEnclosures((prev) => prev.map(
          (e) => (idOf(e.id) === key ? { ...e, ...updated } : e)));
        // Its occupants are re-asked next time the drawer opens.
        invalidateAnimalsFor(key);
      }
      return { ok: true, error: null, enclosure: updated || null };
    } catch (err) {
      if (aliveRef.current) writeEnclosures(snapshot);
      return { ok: false, error: rackApiError(err), enclosure: null };
    }
  }, [facilityId, writeEnclosures, invalidateAnimalsFor]);

  /** Same contract as moveTank, for an egg cup (clutch) on a shelf. */
  const moveCup = useCallback(async (groupId, shelfId, positionNo) => {
    const key = idOf(groupId);
    const snapshot = clutchesRef.current;
    writeClutches((prev) => prev.map((c) => (idOf(c.id) === key
      ? { ...c, shelf: shelfId ?? null, position_no: positionNo ?? null }
      : c)));
    try {
      const updated = await placeAnimalGroup(facilityId, key, { shelf: shelfId, positionNo });
      if (aliveRef.current) {
        writeClutches((prev) => prev.map(
          (c) => (idOf(c.id) === key ? { ...c, ...updated } : c)));
      }
      return { ok: true, error: null };
    } catch (err) {
      if (aliveRef.current) writeClutches(snapshot);
      return { ok: false, error: rackApiError(err) };
    }
  }, [facilityId, writeClutches]);

  return {
    loading,
    refreshing,
    error,
    racks: tree.racks,
    freestanding: tree.freestanding,
    rooms: tree.rooms,
    archivedRacks,
    archivedTanks,
    dropRoom,
    unplacedClutches: tree.unplacedClutches,
    enclosures,
    shelvesById,
    animalsByEnclosure,
    animalsStatus,
    loadAnimalsFor,
    invalidateAnimalsFor,
    clutchesByEnclosure,
    refresh,
    moveTank,
    moveCup,
  };
}
